import { Core } from "@faire/design-tokens";
import { IDropdownOption } from "@faire/help-center-utils/components/QuickActions/ActionModal/types";
import Fuse from "fuse.js";
import * as React from "react";
import { useEffect, useRef, useState } from "react";

import {
  SearchField,
  SelectedValue,
  SelectField,
  SelectFieldChevron,
  SelectFieldWrapper,
  SelectOptions,
} from "./styles";

interface ISelectOption extends IDropdownOption {
  value: string;
}

export interface ISelectOptionProps {
  option: ISelectOption;
  onSelect?: (option: ISelectOption) => void;
}

interface ISearchableSelectProps {
  onSelect?: (selectedOption: ISelectOption) => void;
  options: ISelectOption[];
  searchKeys: string[];
  disabled?: boolean;
  renderOption: (option: ISelectOptionProps) => React.ReactNode;
  placeholder?: string;
}

type IProps = React.PropsWithChildren<ISearchableSelectProps>;

export const SearchableSelect: React.FC<IProps> = (props: IProps) => {
  const fuse = useRef(newFuse(props.options, props.searchKeys));
  useEffect(() => {
    if (props.options?.length) {
      fuse.current = newFuse(props.options, props.searchKeys);
    }
  }, [props.options, props.searchKeys]);

  const [selected, setSelected] = useState<ISelectOption | undefined>();
  const [filteredOptions, setFilteredOptions] = useState<
    ISelectOption[] | undefined
  >();
  const [showOptions, setShowOptions] = useState<boolean>(false);

  const toggleOptions = () => setShowOptions(!showOptions);
  const closeOptions = () => setShowOptions(false);
  const onSelect = (selected: ISelectOption) => {
    props.onSelect?.(selected);
    setSelected(selected);
    setShowOptions(false);
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const searchTerm = event.currentTarget.value;
    if (!searchTerm) {
      setFilteredOptions(undefined);
      return;
    }
    const filteredOptions = fuse.current
      ?.search(searchTerm)
      .map((result) => result.item);
    setFilteredOptions(filteredOptions);
  };

  const disabled = !props.options.length || !!props.disabled;

  return (
    <SelectFieldWrapper onClickOutside={closeOptions}>
      <SelectField
        disabled={disabled}
        onClick={!disabled ? toggleOptions : undefined}
      >
        {selected ? (
          <SelectedValue>{props.children}</SelectedValue>
        ) : (
          <SearchField
            disabled={disabled}
            placeholder={props.placeholder}
            onChange={handleSearch}
          />
        )}
        <SelectFieldChevron color={Core.icon.subdued} direction="down" />
      </SelectField>
      {showOptions ? (
        <SelectOptions>
          {(filteredOptions || props.options).map((option) => (
            <React.Fragment key={option.value}>
              {props.renderOption({ option, onSelect: onSelect })}
            </React.Fragment>
          ))}
        </SelectOptions>
      ) : null}
    </SelectFieldWrapper>
  );
};

const newFuse = (options: ISelectOption[], searchKeys: string[]) => {
  return new Fuse(options, {
    minMatchCharLength: 2,
    keys: searchKeys,
  });
};
